import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Analytics from "analytics";
import logging from "utils/logging";
import { selectCurrentUser } from "features/auth/authSlice";
import { selectUserId } from "features/player/playerSlice";

/**
 * AnalyticsProvider — wires the Analytics façade to React app lifecycle.
 *
 * Responsibilities:
 *   1. Fire page() on every React Router location change (SPA page tracking).
 *   2. Identify creators when Clerk user resolves.
 *   3. Identify anonymous players when Firebase anon uid resolves.
 *   4. Reset identity on Clerk sign-out.
 *
 * Place this inside <BrowserRouter> so useLocation() is available,
 * and inside Redux <Provider> so useSelector() works.
 */
const AnalyticsProvider = ({ children }) => {
  const location = useLocation();
  const authedUser = useSelector(selectCurrentUser);
  const anonUserId = useSelector(selectUserId);
  const prevAuthedRef = useRef(null);
  const identifiedRef = useRef(null);

  // Wire Analytics into logging so log.error() routes to PostHog
  useEffect(() => {
    logging.setAnalytics(Analytics);
  }, []);

  // Page tracking on route change
  useEffect(() => {
    Analytics.page(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Creator identity (Clerk-authed user)
  useEffect(() => {
    if (authedUser && !authedUser.isAnonymous && authedUser.uid) {
      // Don't re-identify if already identified as this user
      if (identifiedRef.current === authedUser.uid) return;
      Analytics.identify(authedUser.uid, {
        email: authedUser.email,
        name: authedUser.displayName,
        role: "creator",
      });
      identifiedRef.current = authedUser.uid;
    }

    // Detect logout: was authed, now null
    if (prevAuthedRef.current && !authedUser) {
      Analytics.reset();
      identifiedRef.current = null;
    }

    prevAuthedRef.current = authedUser;
  }, [authedUser]);

  // Anonymous player identity (Firebase anon uid)
  useEffect(() => {
    // Only identify anon players when there's no authed creator
    if (anonUserId && !authedUser && identifiedRef.current !== anonUserId) {
      Analytics.identify(anonUserId, { role: "player" });
      identifiedRef.current = anonUserId;
    }
  }, [anonUserId, authedUser]);

  return children;
};

export default AnalyticsProvider;
